import { useState } from 'react';
import Button from '../../../../components/form-control/Button';
import TextArea from '../../../../components/form-control/TextArea';
import { Policy } from '../../../../models/policy/Policy';
import { FleetPolicy } from '../../../../models/policy/FleetPolicy';
import { rejectPolicyToDraft } from '../../../../api/requests';
import { showToast } from '../../../../utils/miscFunctions';
import { IRequestResponse } from '../../../../resources/interfaces';
import { RotateCcw } from 'lucide-react';

interface IRejectToDraftProps {
  policy: Policy | FleetPolicy | undefined;
  onProceed: () => void;
  onCancel: () => void;
}

// Insurer-side reason-taker. Distinct modal from ReviewPolicy because
// reject-to-draft targets a different endpoint (POST /reject-to-draft)
// with a smaller payload (just reason) and the copy needs to make the
// consequence explicit — this reopens the policy to full broker edits,
// not just a note-write.
const RejectToDraft = ({ policy, onProceed, onCancel }: IRejectToDraftProps) => {
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  // FleetPolicy carries fleetNumber; single Policy carries ref. The
  // BE endpoint takes either as policyNumber, so use whichever is set.
  const policyIdentifier = (policy as any)?.ref ?? (policy as any)?.fleetNumber ?? '';

  const handleSubmit = () => {
    if (!reason.trim()) {
      showToast.error('Please provide a reason so the broker sees why the policy was reopened.');
      return;
    }
    setSubmitting(true);
    rejectPolicyToDraft(policyIdentifier, { reason: reason.trim() })
      .then((response: any) => {
        showToast.success(response?.message ?? 'Policy returned to Draft.');
        onProceed();
      })
      .catch((error: IRequestResponse) => {
        showToast.error(error?.description ?? 'Unable to return the policy to Draft. Please try again.');
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-amber-100 p-2 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
          <RotateCcw className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold dark:text-darkBg-mainText">Reject policy back to Draft</h2>
          <p className="text-sm text-gray-600 dark:text-darkBg-subText">The broker will regain full edit rights on this policy.</p>
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        Use this only when the broker asked for it to correct a locked field
        (sum insured, cover type, make / model, timeline, usage). Payment
        records stay in place — finance handles the refund separately.
      </div>

      <TextArea
        label="Reason"
        name="reason"
        value={reason}
        onChange={(e: any) => setReason(e?.target?.value ?? '')}
        placeholder="Explain what needs to be corrected so the broker knows what to fix on the reopened policy."
        rows={5}
        required
      />

      <div className="flex justify-end gap-3">
        <Button label="Cancel" bgColor="secondary" onClick={onCancel} disabled={submitting} />
        <Button
          label={submitting ? 'Returning to Draft...' : 'Return to Draft'}
          bgColor="primary"
          onClick={handleSubmit}
          disabled={submitting || !reason.trim()}
        />
      </div>
    </div>
  );
};

export default RejectToDraft;
